<template>
    <transition name="fade">
        <div v-if="visible && noError" class="controls-changed-notification-container">
            <span class="notification-text" v-html="notificationMessage"></span>
        </div>
    </transition>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';

@Component
export default class ControlsChangedNotificationMobile extends Vue {
    visible = false;
    changedToValue = '';
    timeoutId = null; // Store timeout ID
    lastNotificationMessage = ''; // Store the last valid notification message
    noError = null;
    @Prop()
    popUpData;

    showNotification(value) {
        this.changedToValue = value;
        this.visible = true;
        // Clear any existing timer before starting a new one
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        // Start a fresh timer to hide after 4 seconds
        this.timeoutId = setTimeout(() => {
            this.visible = false;
            this.timeoutId = null; // Reset timeout ID
        }, 4000);
    }

    mounted() {
        window.addEventListener('show-notification', this.setNoErrorToTrue);
        window.addEventListener('hide-notification', this.setNoErrorToFalse);
    }

    beforeDestroy() {
        window.removeEventListener('show-notification', this.setNoErrorToTrue);
        window.removeEventListener('hide-notification', this.setNoErrorToFalse);
    }

    get notificationMessage() {
        if (!this.popUpData.confirmedChoice) return this.lastNotificationMessage || null;

        const type = this.confirmedPopUpType;

        switch (type) {
            case 'INVERTER':
            case 'AC-SYSTEM':
            case 'INVERTER-RS':
            case 'MULTI-RS':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.change-inverter-mode',
                    {
                        i18nFallback: `The inverter mode has been changed to <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'INVERTER-GRID-LIMIT':
            case 'AC-SYSTEM-GRID-LIMIT':
            case 'CHARGING-STATION-CURRENT':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.change-current-limit',
                    {
                        i18nFallback: `The input current limit has been changed to <b>${this.resultValue} A </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'ESS':
            case 'AC-ESS':
            case 'Ac-System':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.change-ess-mode',
                    {
                        i18nFallback: `The ESS mode has been changed to <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'ESS-BATTERY':
            case 'AC-ESS-BATTERY':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.change-min-soc',
                    {
                        i18nFallback: `The minimum SOC has been changed to <b>${this.resultValue} % </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'SWITCH-DYNAMIC-ESS':
            case 'SWITCH-DYNAMIC-AC-ESS':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.change-dynamic-ess',
                    {
                        i18nFallback: `Dynamic ESS has been turned <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'SWITCH-RELAYS': {
                const relayIndex = Number(this.popUpData.confirmedChoice.confirmedRelayIdx) + 1;
                this.lastNotificationMessage = `Relay ${relayIndex} has been turned  <b>${
                    this.changedToValue ? 'on' : 'off'
                } </b>`;
                break;
            }

            case 'SWITCH-AUTO-START':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.auto-start',
                    {
                        i18nFallback: `Autostart has been turned <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'SWITCH-TIMED-RUN':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.timed-run',
                    {
                        i18nFallback: `Timed run has been turned <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'SWITCH-MANUAL-START':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.manual-start',
                    {
                        i18nFallback: `Generator has been started </b>`,
                    },
                );
                break;

            case 'SWITCH-MANUAL-STOP':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.manual-stop',
                    {
                        i18nFallback: `Generator has been stopped </b>`,
                    },
                );
                break;

            case 'SWITCH-RESET-SERVICE':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.reset-service',
                    {
                        i18nFallback: `Service has been reset </b>`,
                    },
                );
                break;

            case 'SWITCH-CHARGING-STATION-CHARGE':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.start-stop',
                    {
                        i18nFallback: `Startstop has been turned <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            case 'CHARGING-STATION':
                this.lastNotificationMessage = this.$t(
                    'dashboard.change_controls_notification.charging-station',
                    {
                        i18nFallback: `The mode has been changed to <b>${this.resultValue} </b>`,
                        resultValue: this.resultValue,
                    },
                );
                break;

            default:
                break;
        }

        return this.lastNotificationMessage || null;
    }

    setNoErrorToTrue() {
        this.noError = true;
        this.visible = true; // Now show the notification only if it's valid
    }
    setNoErrorToFalse() {
        this.noError = false;
    }

    get resultValue() {
        if (this.popUpData.confirmedChoice.confirmedPopUpType.includes('SWITCH')) {
            return this.changedToValue ? 'on' : 'off';
        }
        return this.changedToValue;
    }

    get confirmedPopUpType() {
        return this.popUpData.confirmedChoice.confirmedPopUpType;
    }
}
</script>
